import { DecoratorNode, NodeKey, SerializedLexicalNode, Spread } from 'lexical';
import styles from '../RichTextEditor.module.scss';

type SerializedImageNode = Spread<
  {
    type: 'image';
    version: 1;
    src: string;
    width?: number;
    height?: number;
    id: string;
  },
  SerializedLexicalNode
>;

export class ImageNode extends DecoratorNode<JSX.Element> {
  __src: string;
  __width: number | undefined;
  __height: number | undefined;
  __id: string;

  static getType(): string {
    return 'image';
  }

  static clone(node: ImageNode): ImageNode {
    return new ImageNode(
      node.__src,
      node.__id,
      node.__width,
      node.__height,
      node.__key,
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-useless-constructor
  constructor(
    src: string = '',
    id: string,
    width?: number,
    height?: number,
    key?: NodeKey,
  ) {
    super(key);
    this.__src = src;
    this.__id = id;
    this.__width = width;
    this.__height = height;
  }

  createDOM(): HTMLElement {
    const wrapper = document.createElement('span');

    wrapper.dataset.nodeKey = this.__key;
    wrapper.className = styles.imageWrapper;
    const img = document.createElement('img');
    img.src = this.__src;
    if (this.__width) img.width = this.__width;
    if (this.__height) img.height = this.__height;
    img.className = styles.image;
    img.draggable = true;

    img.addEventListener('dragstart', (e) => {
      e.dataTransfer?.setData('image-node', this.__key);
      if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
    });

    const handleDiagonal = document.createElement('div');
    handleDiagonal.className = styles.imageResizeHandle;
    handleDiagonal.dataset.resizeHandle = 'diagonal';

    const handleRight = document.createElement('div');
    handleRight.dataset.resizeHandle = 'right';
    handleRight.className = styles.imageResizeHandleRight;

    const handleBottom = document.createElement('div');
    handleBottom.dataset.resizeHandle = 'bottom';
    handleBottom.className = styles.imageResizeHandleBottom;

    wrapper.appendChild(img);
    wrapper.appendChild(handleDiagonal);
    wrapper.appendChild(handleRight);
    wrapper.appendChild(handleBottom);

    return wrapper;
  }

  updateDOM(prevNode: ImageNode, dom: HTMLElement): boolean {
    const img = dom.querySelector('img');
    if (!img) return false;

    let updated = false;

    if (prevNode.__src !== this.__src) {
      img.src = this.__src;
      updated = true;
    }
    return updated;
  }

  static importJSON(json: SerializedImageNode): ImageNode {
    return new ImageNode(json.src, json.id, json.width, json.height);
  }

  exportJSON(): SerializedImageNode {
    return {
      type: 'image',
      version: 1,
      src: this.__src,
      id: this.__id,
      width: this.__width,
      height: this.__height,
    };
  }

  setWidth(width: number) {
    const writable = this.getWritable();
    writable.__width = width;
  }

  setHeight(height: number) {
    const writable = this.getWritable();
    writable.__height = height;
  }

  setSrc(urlByIds: Record<string, string>) {
    var url = urlByIds[this.__id];
    const writable = this.getWritable();
    writable.__src = url;
  }
}

export function createImageNode(src: string, id: string): ImageNode {
  return new ImageNode(src, id);
}
